/**
 * Single source of truth for the site's SEO metadata.
 *
 * `SITE` holds the values shared by every page; `PAGE_SEO` overrides them per
 * static route. Dynamic routes (blog posts, practitioner profiles) build their
 * metadata from the record they render and pass it to <Seo /> directly.
 *
 * The per-route table lives in pageSeo.json rather than here so the build-time
 * prerender script can require() the same file the app imports — one list of
 * routes, no drift between what ships and what gets prerendered.
 */
import PAGE_SEO_JSON from './pageSeo.json';

// The canonical host. Every canonical/OG URL is absolute and points here, so a
// page reached over the bare apex or a staging host still declares the one URL
// we want indexed.
export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://www.mynirva.com').replace(
  /\/$/,
  ''
);

export const SITE = {
  name: 'Nirva',
  legalName: 'Nirva – Interpersonal Hypnotherapy Clinic',
  url: SITE_URL,
  // TODO: replace with a purpose-built 1200x630 social card. The current logo
  // is 400x400, which every platform crops to a square thumbnail.
  ogImage: `${SITE_URL}/logo.webp`,
  ogImageWidth: 400,
  ogImageHeight: 400,
  locale: 'en_US',
  twitterCard: 'summary_large_image',
};

export const DEFAULT_DESCRIPTION =
  'Nirva is an online hypnotherapy clinic offering confidential sessions with certified Interpersonal Hypnotherapy practitioners. Book a free 30-minute consultation.';

/** Appends the brand to a page title unless the title already carries it. */
export const buildTitle = (title) => {
  if (!title) return `${SITE.name} | Online Hypnotherapy Clinic`;
  return title.includes(SITE.name) ? title : `${title} | ${SITE.name}`;
};

/** Turns a router path into an absolute canonical URL. */
export const absoluteUrl = (path = '/') => {
  if (!path) return SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

/**
 * Static routes only. Keyed by the exact router path.
 *
 * `noindex` marks pages that should stay out of the index: funnel steps with no
 * standalone content, authentication screens, and the practitioner route that
 * duplicates /practitioner-profile.
 */
/**
 * Static routes only, keyed by the exact router path. See pageSeo.json.
 *
 * `noindex` marks pages that should stay out of the index: funnel steps with no
 * standalone content, and authentication screens.
 */
export const PAGE_SEO = PAGE_SEO_JSON;

/** Routes that must never be prerendered or indexed, including dynamic ones. */
export const NOINDEX_PATTERNS = [
  /^\/booking/,
  /^\/login/,
  /^\/forgot-password/,
  /^\/reset-password/,
  /^\/test-messages/,
  // Duplicates /practitioner-profile/:name and is only reachable from the
  // booking flow, so it would split ranking signals for the same person.
  /^\/practitioner-detail/,
];

/** Convenience lookup used by pages that just need their static entry. */
export const getPageSeo = (path) => PAGE_SEO[path] || {};
